import { $, Enable, html, on, View } from 'rune-ts';
import { ButtonClose } from '../../ButtonClose/ButtonClose';
import { ButtonShow } from '../../ButtonShow/ButtonShow';
import { InputBase } from '../InputBase';
import klass from './InputSide.module.scss';
import { InputProps } from '../type';
import {
  InputChangedEvent,
  InputFocusinEvent,
  InputFocusoutEvent,
  InputResetAfterEvent,
  InputResetEvent,
  InputShowEvent,
} from '../InputEvent';

export class InputSideController<D extends InputProps> extends View<D> {
  resetButton: ButtonClose;
  showButton?: ButtonShow;

  constructor(data: D) {
    super(data);

    this.resetButton = new ButtonClose({ hide: true, tag: 'span' });
    if (data.type === 'password') {
      this.showButton = new ButtonShow({ show: false, tag: 'span' });
    }
  }

  protected override template() {
    const { value, disabled, readonly, type } = this.data;
    return html` <div
      data-input-start="${type === 'password' ? true : !!value && !(disabled || readonly)}"
      class="${klass.controller}"
    >
      ${this.resetButton} ${this.showButton ? this.showButton : ''}
    </div>`;
  }

  protected override onRender() {
    this.delegate('click', ButtonClose, () => {
      this.dispatchEvent(InputResetEvent, { bubbles: true });
    });

    if (this.showButton) {
      this.delegate('click', ButtonShow, (e, view) => {
        view.toggleShow();
        view.redraw();
        this.dispatchEvent(InputShowEvent, { bubbles: true, detail: view.isShow() });
      });
    }
  }

  override redraw(): this {
    const { value, disabled, readonly, type } = this.data;

    $(this.element()).setAttribute(
      'data-input-start',
      type === 'password' ? true : !!value && !(disabled || readonly),
    );

    if (value && this.resetButton.data.hide) {
      this.resetButton.show();
    }

    if (!value) {
      this.resetButton.immediateHide();
    }

    return this;
  }

  focusout() {
    this.resetButton.hide();
  }
}

export class InputSideControllerEnable<
  D extends InputProps,
  V extends InputBase<D, InputSideController<D>> = InputBase<D, InputSideController<D>>,
> extends Enable<D> {
  constructor(public override view: V) {
    super(view);
  }

  @on(InputShowEvent)
  showHandler(e: InputShowEvent) {
    this.view.inputView.type = e.detail ? 'text' : 'password';
    this.view.inputView.redraw();
  }

  @on(InputResetEvent)
  resetHandler() {
    this.data.value = this.data.type === 'number' ? 0 : '';
    this.view.inputView.reset();
    this.view.inputSideView.redraw();
    this.dispatchEvent(InputResetAfterEvent, {});
  }

  @on(InputFocusinEvent)
  @on(InputChangedEvent)
  redrawHandler() {
    this.view.inputSideView.redraw();
  }

  @on(InputFocusoutEvent)
  focusoutHandler() {
    this.view.inputSideView.focusout();
  }
}
